import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { doc, getDoc, collection, query, where, orderBy, onSnapshot, getDocs, writeBatch, Timestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import {
    IoArrowBack, IoWalletOutline, IoCarSportOutline, IoDocumentTextOutline,
    IoCalendarOutline, IoBriefcaseOutline, IoCashOutline, IoClose, IoPencil, IoWarning,
    IoPersonCircleOutline, IoKeyOutline, IoHomeOutline, IoTimeOutline, IoCheckmarkCircleOutline,
    IoMailOutline, IoCallOutline, IoPeopleOutline
} from 'react-icons/io5';
import moment from 'moment';
import './GestionUsuariosScreen.css'; // Shared modern styles
import './DetalleUsuarioScreen.css'; // Screen-specific styles
import EditarTransaccionModal from '../components/FinancialReports/EditarTransaccionModal';
import { useAuth } from '../context/AuthContext';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

// --- Reusable Badges (Shared style) ---
const StatusBadge = ({ status }) => {
    let className = 'status-pill inquilino'; // default
    let icon = null;
    let label = status;

    if (status === 'pagado') {
        className = 'status-pill status-aldia';
        icon = <IoCheckmarkCircleOutline />;
    } else if (status === 'pendiente') {
        className = 'status-pill inquilino'; // Gray
        icon = <IoTimeOutline />;
    } else if (status === 'vencida') {
        className = 'status-pill status-moroso';
        icon = <IoWarning />;
    } else if (status === 'parcial') {
        className = 'status-pill';
        label = 'Parcial';
        icon = <IoWalletOutline />;
        // Custom style for parcial if needed, or stick to default
    }

    return (
        <span className={className} style={{ textTransform: 'capitalize' }}>
            {icon} {label}
        </span>
    );
};

function DetalleUsuarioScreen() {
    const { usuarioId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Data States
    const [usuario, setUsuario] = useState(null);
    const [cargos, setCargos] = useState([]);
    const [reportes, setReportes] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [reservas, setReservas] = useState([]);
    const [pagos, setPagos] = useState([]);
    const [proveedorInfo, setProveedorInfo] = useState(null);
    const [amenidadesMap, setAmenidadesMap] = useState({});

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('historial');
    const [cargosLoaded, setCargosLoaded] = useState(false); // Fix race condition
    const [apercibimientos, setApercibimientos] = useState([]);
    const [convenios, setConvenios] = useState([]);

    // --- Edit Modal State ---
    const [editingRef, setEditingRef] = useState(null); // { id, type }

    // --- Table Filters ---
    const [cargosFilters, setCargosFilters] = useState({
        estatus: '',
        concepto: '',
        vencimiento: '',
        monto: '',
        metodo: ''
    });

    // --- Manual Payment States ---
    const [selectedCharges, setSelectedCharges] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({
        fechaPago: moment().format('YYYY-MM-DDTHH:mm'),
        metodoPago: 'Transferencia',
        referencia: ''
    });

    // --- Suspension States ---
    const [showSuspensionModal, setShowSuspensionModal] = useState(false);
    const [suspensionDuracion, setSuspensionDuracion] = useState('1_dia');
    const [suspensionMotivo, setSuspensionMotivo] = useState('');
    const [isSavingSuspension, setIsSavingSuspension] = useState(false);

    // --- Suspension Action Handlers ---
    const handleSuspenderReportes = async () => {
        if (!suspensionMotivo.trim()) {
            alert('Por favor, escribe un motivo para la suspensión.');
            return;
        }

        setIsSavingSuspension(true);
        try {
            let hasta = moment();
            if (suspensionDuracion === '1_dia') hasta.add(1, 'days');
            else if (suspensionDuracion === '1_semana') hasta.add(7, 'days');
            else if (suspensionDuracion === '1_mes') hasta.add(1, 'months');
            else if (suspensionDuracion === '1_ano') hasta.add(1, 'years');

            const userRef = doc(db, 'usuarios', usuarioId);
            await updateDoc(userRef, {
                suspensionReportes: {
                    activo: true,
                    desde: Timestamp.now(),
                    hasta: Timestamp.fromDate(hasta.toDate()),
                    motivo: suspensionMotivo.trim(),
                    suspendidoPor: currentUser?.uid || 'admin'
                }
            });

            setShowSuspensionModal(false);
            setSuspensionMotivo('');
            alert('El acceso a reportes ha sido suspendido correctamente.');
        } catch (error) {
            console.error('Error al suspender reportes:', error);
            alert('Error al aplicar la suspensión: ' + error.message);
        } finally {
            setIsSavingSuspension(false);
        }
    };

    const handleLevantarSuspension = async () => {
        if (!window.confirm('¿Estás seguro de que deseas levantar la suspensión de reportes para este residente?')) return;

        try {
            const userRef = doc(db, 'usuarios', usuarioId);
            await updateDoc(userRef, {
                'suspensionReportes.activo': false
            });
            alert('La suspensión de reportes ha sido levantada.');
        } catch (error) {
            console.error('Error al levantar la suspensión:', error);
            alert('Error al levantar la suspensión: ' + error.message);
        }
    };

    useEffect(() => {
        if (!usuarioId) return;

        let unsubUser;
        let unsubCargos;
        let unsubReportes;
        let unsubVehiculos;
        let unsubReservas;
        let unsubApercibimientos;
        let unsubConvenios;
        let unsubPagos;

        const fetchData = async () => {
            try {
                // 1. Fetch User Data with onSnapshot for real-time reactivity
                unsubUser = onSnapshot(doc(db, "usuarios", usuarioId), (docSnap) => {
                    if (docSnap.exists()) {
                        setUsuario({ id: docSnap.id, ...docSnap.data() });
                    }
                });

                // 2. Fetch Provider Status
                const qProv = query(collection(db, "market_proveedores"), where("usuarioId", "==", usuarioId));
                getDocs(qProv).then(snap => {
                    if (!snap.empty) {
                        setProveedorInfo({ id: snap.docs[0].id, ...snap.docs[0].data() });
                    }
                });

                // Listeners
                unsubCargos = onSnapshot(query(collection(db, "cargos"), where("residenteId", "==", usuarioId), orderBy("fechaVencimiento", "desc")), (snap) => {
                    setCargos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                    setCargosLoaded(true);
                });

                unsubReportes = onSnapshot(query(collection(db, "reportes"), where("residenteId", "==", usuarioId), orderBy("fechaCreacion", "desc")), (snap) => {
                    setReportes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                });

                unsubVehiculos = onSnapshot(query(collection(db, "vehiculos"), where("residenteId", "==", usuarioId)), (snap) => {
                    setVehiculos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                });

                unsubReservas = onSnapshot(query(collection(db, "reservaciones"), where("residenteId", "==", usuarioId)), (snap) => {
                    const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    lista.sort((a, b) => b.fechaInicio.seconds - a.fechaInicio.seconds);
                    setReservas(lista);
                });

                unsubApercibimientos = onSnapshot(query(collection(db, "apercibimientos_multas"), where("residenteId", "==", usuarioId)), (snap) => {
                    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    list.sort((a, b) => {
                        const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
                        const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
                        return dateB - dateA;
                    });
                    setApercibimientos(list);
                });

                unsubConvenios = onSnapshot(query(collection(db, "convenios"), where("residenteId", "==", usuarioId)), (snap) => {
                    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    list.sort((a, b) => {
                        const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
                        const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
                        return dateB - dateA;
                    });
                    setConvenios(list);
                });

                unsubPagos = onSnapshot(query(collection(db, "movimientos_bancarios"), where("residenteId", "==", usuarioId), orderBy("fecha", "desc")), (snap) => {
                    setPagos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                });

                // Fetch Amenidades Map
                getDocs(collection(db, "amenidades")).then(snap => {
                    const mapping = {};
                    snap.docs.forEach(doc => {
                        mapping[doc.id] = doc.data().nombre;
                    });
                    setAmenidadesMap(mapping);
                }).catch(err => console.error("Error fetching amenidades map:", err));

                setLoading(false);

            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            if (unsubUser) unsubUser();
            if (unsubCargos) unsubCargos();
            if (unsubReportes) unsubReportes();
            if (unsubVehiculos) unsubVehiculos();
            if (unsubReservas) unsubReservas();
            if (unsubApercibimientos) unsubApercibimientos();
            if (unsubConvenios) unsubConvenios();
            if (unsubPagos) unsubPagos();
        };
    }, [usuarioId]);

    // --- Manual Payment Logic ---
    const [paymentMode, setPaymentMode] = useState('manual'); // 'manual' | 'bank'
    const [unappliedDeposits, setUnappliedDeposits] = useState([]);
    const [selectedDepositId, setSelectedDepositId] = useState(null);

    const toggleChargeSelection = (chargeId) => {
        setSelectedCharges(prev => {
            if (prev.includes(chargeId)) {
                return prev.filter(id => id !== chargeId);
            } else {
                return [...prev, chargeId];
            }
        });
    };

    // Load deposits when switching to bank mode
    useEffect(() => {
        if (showPaymentModal && paymentMode === 'bank') {
            const q = query(collection(db, "movimientos_bancarios"),
                where("estatus", "in", ["nuevo", "identificado"]),
                orderBy("fecha", "desc")
            );
            getDocs(q).then(snap => {
                setUnappliedDeposits(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });
        }
    }, [showPaymentModal, paymentMode]);

    const handleRegisterPayment = async () => {
        if (selectedCharges.length === 0) return;

        // Validation
        if (paymentMode === 'manual' && !paymentDetails.referencia.trim()) {
            alert("Por seguridad, debes escribir un Motivo o Referencia para el pago manual.");
            return;
        }
        if (paymentMode === 'bank' && !selectedDepositId) {
            alert("Debes seleccionar un depósito bancario para vincular.");
            return;
        }

        if (!window.confirm(`¿Confirmas registrar el pago para ${selectedCharges.length} cargo(s) ? `)) return;

        try {
            const batch = writeBatch(db);
            const fechaPagoTimestamp = Timestamp.fromDate(new Date(paymentDetails.fechaPago));

            let depositInfo = null;
            if (paymentMode === 'bank') {
                const deposit = unappliedDeposits.find(d => d.id === selectedDepositId);
                depositInfo = deposit;

                // Update Deposit Status
                const depositRef = doc(db, "movimientos_bancarios", selectedDepositId);
                batch.update(depositRef, {
                    estatus: 'conciliado',
                    residenteId: usuario.id,
                    residenteNombre: usuario.nombre,
                    fechaConciliacion: Timestamp.now(),
                    // We link to the first charge ID just as a reference, or multiple?
                    // For simplicity, we link to the user/transaction.
                    cargoId: selectedCharges[0]
                });
            }

            // Update Charges
            selectedCharges.forEach(chargeId => {
                const chargeRef = doc(db, "cargos", chargeId);
                const updates = {
                    estatus: 'pagado',
                    fechaPago: fechaPagoTimestamp,
                    registradoPor: 'Administrador'
                };

                if (paymentMode === 'bank' && depositInfo) {
                    updates.metodoPago = 'Depósito Bancario';
                    updates.referenciaPago = depositInfo.referencia;
                    updates.notas = `Conciliado con depósito ${depositInfo.fechaString} ($${depositInfo.monto})`;
                } else {
                    updates.metodoPago = paymentDetails.metodoPago;
                    updates.referenciaPago = paymentDetails.referencia; // Contains Description
                }

                batch.update(chargeRef, updates);
            });

            // Update Resident Status Logic
            const allPendingDebt = cargos
                .filter(c => c.estatus === 'pendiente' || c.estatus === 'parcial')
                .reduce((sum, c) => sum + (c.monto - (c.montoPagado || 0)), 0);

            const amountBeingPaid = cargos
                .filter(c => selectedCharges.includes(c.id))
                .reduce((sum, c) => sum + (c.monto - (c.montoPagado || 0)), 0);

            const remainingDebt = allPendingDebt - amountBeingPaid;
            const isClean = remainingDebt < 1.0;

            const userRef = doc(db, "usuarios", usuario.id);
            batch.update(userRef, { isMoroso: !isClean });

            await batch.commit();
            alert("Pago registrado correctamente.");
            setShowPaymentModal(false);
            setSelectedCharges([]);
            // Reset States
            setSelectedDepositId(null);
            setPaymentMode('manual');
            setPaymentDetails({
                fechaPago: moment().format('YYYY-MM-DDTHH:mm'),
                metodoPago: 'Transferencia',
                referencia: ''
            });

        } catch (error) {
            console.error("Error registering payment:", error);
            alert("Hubo un error al registrar el pago.");
        }
    };

    const handleEditSuccess = () => {
        setEditingRef(null);
        // The snapshots will auto-update the views
        alert("Registro actualizado correctamente.");
    };

    // Calculate Stats
    const totalDeuda = cargos
        .filter(c => c.estatus === 'pendiente' || c.estatus === 'parcial')
        .reduce((sum, c) => sum + (Number(c.monto) - (c.montoPagado || 0)), 0);

    const deudaSeleccionada = cargos
        .filter(c => selectedCharges.includes(c.id))
        .reduce((sum, c) => sum + (Number(c.monto) - (c.montoPagado || 0)), 0);

    // Auto-update 'isMoroso' status if incorrect
    useEffect(() => {
        if (!usuario || loading || !cargosLoaded) return; // Wait for REAL data

        const checkMorosoStatus = async () => {
            const ahora = new Date();
            const hasOverdue = cargos.some(c => {
                const isPending = c.estatus === 'pendiente' || c.estatus === 'parcial';
                let vencimiento;
                try {
                    vencimiento = c.fechaVencimiento?.toDate ? c.fechaVencimiento.toDate() : new Date(c.fechaVencimiento);
                } catch (e) {
                    return false;
                }
                return isPending && vencimiento < ahora;
            });

            let shouldBeMoroso = hasOverdue;

            // Validar si cuenta con convenio activo
            let hasActiveConvenio = false;
            try {
                const qConvenios = query(
                    collection(db, "convenios"),
                    where("condominioId", "==", usuario.condominioId),
                    where("residenteId", "==", usuario.id),
                    where("estatus", "==", "activo")
                );
                const snapConvenios = await getDocs(qConvenios);
                hasActiveConvenio = snapConvenios.size > 0;
                
                if (hasOverdue && hasActiveConvenio) {
                    const condoSnap = await getDoc(doc(db, "condominios", usuario.condominioId));
                    if (condoSnap.exists()) {
                        const activeAccess = condoSnap.data().conveniosConfig?.activeAccess || 'completo';
                        if (activeAccess === 'completo') {
                            shouldBeMoroso = false;
                        }
                    }
                }
            } catch (e) {
                console.error("Error checking convenios in DetalleUsuario:", e);
            }

            const currentMoroso = usuario.isMoroso === true;
            const currentConvenio = usuario.hasConvenio === true;

            if (shouldBeMoroso !== currentMoroso || hasActiveConvenio !== currentConvenio) {
                console.log(`Auto - correcting isMoroso/hasConvenio for ${usuario.nombre}: isMoroso=${shouldBeMoroso}, hasConvenio=${hasActiveConvenio}`);
                const userRef = doc(db, "usuarios", usuario.id);
                updateDoc(userRef, { isMoroso: shouldBeMoroso, hasConvenio: hasActiveConvenio }).catch(e => console.error("Error auto-updating moroso:", e));
                setUsuario(prev => ({ ...prev, isMoroso: shouldBeMoroso, hasConvenio: hasActiveConvenio }));
            }
        };

        checkMorosoStatus();
    }, [usuario, cargos, loading, cargosLoaded]);

    const filteredCargos = cargos.filter(cargo => {
        const isExpired = cargo.estatus === 'pendiente' && cargo.fechaVencimiento && moment().isAfter(moment(cargo.fechaVencimiento.toDate()));
        const displayStatus = isExpired ? 'vencida' : cargo.estatus;

        if (cargosFilters.estatus && cargosFilters.estatus !== 'todos') {
            if (cargosFilters.estatus !== displayStatus) return false;
        }

        if (cargosFilters.concepto) {
            const searchStr = cargosFilters.concepto.toLowerCase();
            const textMatch = `${cargo.tipo || ''} ${cargo.descripcion || ''}`.toLowerCase();
            if (!textMatch.includes(searchStr)) return false;
        }

        if (cargosFilters.vencimiento) {
            const query = cargosFilters.vencimiento.trim().toLowerCase();
            const cargoMoment = cargo.fechaVencimiento ? moment(cargo.fechaVencimiento.toDate()) : null;
            if (cargoMoment) {
                if (query.startsWith('>')) {
                    const d = moment(query.substring(1).trim(), ['DD/MM/YYYY', 'YYYY-MM-DD']);
                    if (d.isValid() && !cargoMoment.isAfter(d)) return false;
                } else if (query.startsWith('<')) {
                    const d = moment(query.substring(1).trim(), ['DD/MM/YYYY', 'YYYY-MM-DD']);
                    if (d.isValid() && !cargoMoment.isBefore(d)) return false;
                } else {
                    const textDate1 = cargoMoment.format('DD/MM/YY').toLowerCase();
                    const textDate2 = cargoMoment.format('DD MMM YYYY').toLowerCase();
                    if (!textDate1.includes(query) && !textDate2.includes(query)) return false;
                }
            } else {
                return false;
            }
        }

        if (cargosFilters.monto) {
            const query = cargosFilters.monto.trim();
            const match = query.match(/^([<>]=?)?\s*(\d+(\.\d+)?)$/);
            if (match) {
                const op = match[1];
                const val = parseFloat(match[2]);
                const monto = Number(cargo.monto);
                if (op === '>' && !(monto > val)) return false;
                if (op === '>=' && !(monto >= val)) return false;
                if (op === '<' && !(monto < val)) return false;
                if (op === '<=' && !(monto <= val)) return false;
                if (!op && monto !== val) return false;
            } else {
                if (!cargo.monto.toString().includes(query)) return false;
            }
        }

        if (cargosFilters.metodo && cargosFilters.metodo !== 'todos') {
            const m = (cargo.metodoPago || '').toLowerCase();
            if (m !== cargosFilters.metodo.toLowerCase()) return false;
        }

        return true;
    });

    if (loading) {
        return (
            <div className="ks-page">
                <KondoSidebar activeRoute="/gestion-usuarios" />
                <main className="ks-main">
                    <KondoBrandHeader showBack={false} />
                    <div className="ks-scroll-area">
                        <div className="usuarios-container" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                            <div className="loading-container" style={{ color: '#64748b', fontSize: '1.1rem' }}>Cargando perfil...</div>
                        </div>
                    </div>
                </main>
                <KondoBottomNav activeRoute="/gestion-usuarios" />
            </div>
        );
    }

    if (!usuario) {
        return (
            <div className="ks-page">
                <KondoSidebar activeRoute="/gestion-usuarios" />
                <main className="ks-main">
                    <KondoBrandHeader showBack={false} />
                    <div className="ks-scroll-area">
                        <div className="usuarios-container" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                            <div className="error-container" style={{ color: '#dc2626', fontSize: '1.1rem' }}>Usuario no encontrado</div>
                        </div>
                    </div>
                </main>
                <KondoBottomNav activeRoute="/gestion-usuarios" />
            </div>
        );
    }

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/gestion-usuarios" />

            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area">
                    <div className="usuarios-container">
                        {/* --- PREMIUM COMPACT BANNER --- */}
                        <div className="usuarios-fixed-wrapper">
                            <div className="usuarios-banner-compact" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)' }}>
                                
                                {/* Back Button */}
                                <button type="button" onClick={() => navigate('/gestion-usuarios')} className="usuarios-back-btn glass">
                                    <IoArrowBack size={20} />
                                </button>

                                {/* User Details Container */}
                                <div className="usuarios-banner-content" style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                                    {usuario.fotoPerfilUrl ? (
                                        <img 
                                            src={usuario.fotoPerfilUrl} 
                                            alt="" 
                                            className="user-avatar" 
                                            style={{ 
                                                width: 68, 
                                                height: 68, 
                                                borderRadius: '50%', 
                                                objectFit: 'cover', 
                                                border: '3px solid rgba(255,255,255,0.4)',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                            }} 
                                        />
                                    ) : (
                                        <div 
                                            className="user-avatar-placeholder" 
                                            style={{ 
                                                width: 68, 
                                                height: 68, 
                                                borderRadius: '50%', 
                                                backgroundColor: 'rgba(255,255,255,0.2)', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                color: 'white', 
                                                fontSize: '2.2em',
                                                border: '3px solid rgba(255,255,255,0.4)',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <IoPersonCircleOutline />
                                        </div>
                                    )}
                                    <div>
                                        <h2 style={{ fontSize: '1.6rem', color: 'white', margin: '0 0 4px 0', fontWeight: 'bold', letterSpacing: '-0.02em' }}>
                                            {usuario.nombre}
                                        </h2>
                                        <p style={{ margin: '0 0 8px 0', color: 'rgba(255,255,255,0.9)', fontSize: '0.92rem', fontWeight: '500' }}>
                                            {usuario.condominioId} - {usuario.numeroCasa || 'Sin Unidad'}
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <span className={`status-pill ${usuario.tipoResidente === 'Propietario' ? 'propietario' : 'inquilino'}`} style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)' }}>
                                                {usuario.tipoResidente === 'Propietario' ? <IoKeyOutline /> : <IoHomeOutline />} {usuario.tipoResidente || 'Inquilino'}
                                            </span>
                                            {usuario.hasConvenio ? (
                                                <span className="status-pill status-convenio" style={{ backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', fontWeight: '600' }}>
                                                    🤝 Convenio
                                                </span>
                                            ) : (
                                                <span className={`status-pill ${usuario.isMoroso ? 'status-moroso' : 'status-aldia'}`} style={{ backgroundColor: usuario.isMoroso ? '#fee2e2' : '#dcfce7', color: usuario.isMoroso ? '#991b1b' : '#166534', border: 'none', fontWeight: '600' }}>
                                                    {usuario.isMoroso ? '⚠️ Moroso' : '✅ Al Corriente'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Balance Card overlay (Right Side) */}
                                <div className="usuarios-actions hide-on-mobile" style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', padding: '12px 24px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)' }}>
                                        <span style={{ display: 'block', fontSize: '0.78em', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>Saldo Pendiente</span>
                                        <span style={{ display: 'block', fontSize: '2em', fontWeight: 'bold', color: 'white', lineHeight: '1.1', marginTop: '2px' }}>
                                            ${new Intl.NumberFormat('es-MX').format(totalDeuda)}
                                        </span>
                                        {usuario.saldoFavor > 0 && (
                                            <span style={{ display: 'block', fontSize: '0.85em', color: '#a7f3d0', marginTop: '4px', fontWeight: '500' }}>
                                                + ${new Intl.NumberFormat('es-MX').format(usuario.saldoFavor)} a favor
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <IoPersonCircleOutline className="usuarios-banner-icon" style={{ opacity: 0.12 }} />
                            </div>
                        </div>

            {/* Mobile Balance Card (Below Banner) */}
            <div className="mobile-actions-row" style={{ marginTop: '5px', marginBottom: '15px' }}>
                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e5e7eb', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <span style={{ display: 'block', fontSize: '0.85em', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saldo Pendiente</span>
                        {usuario.saldoFavor > 0 && <span style={{ display: 'block', fontSize: '0.85em', color: '#22c55e' }}>+ ${new Intl.NumberFormat('es-MX').format(usuario.saldoFavor)} a favor</span>}
                    </div>
                    <span style={{ display: 'block', fontSize: '1.6em', fontWeight: 'bold', color: '#1f2937' }}>${new Intl.NumberFormat('es-MX').format(totalDeuda)}</span>
                </div>
            </div>

            {/* --- NAVIGATION TABS --- */}
            <div className="detalle-tabs-container">
                {[
                    { id: 'historial', icon: <IoWalletOutline />, label: 'Financiero' },
                    { id: 'perfil', icon: <IoPersonCircleOutline />, label: 'Datos' },
                    { id: 'reportes', icon: <IoDocumentTextOutline />, label: `Reportes(${reportes.length})` },
                    { id: 'vehiculos', icon: <IoCarSportOutline />, label: `Vehículos(${vehiculos.length})` },
                    { id: 'reservas', icon: <IoCalendarOutline />, label: `Reservas(${reservas.length})` },
                    { id: 'apercibimientos', icon: <IoWarning />, label: `Apercibimientos(${apercibimientos.length})` },
                    { id: 'convenios', icon: <IoDocumentTextOutline />, label: `Convenios(${convenios.length})` },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`detalle-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* --- CONTENT AREA --- */}
            <div className="usuarios-card detalle-content-card">

                {activeTab === 'historial' && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 20px 0 20px', marginBottom: 20, alignItems: 'center' }}>
                            <h3 style={{ margin: 0, color: '#1f2937' }}>Cargos del Residente</h3>
                            {selectedCharges.length > 0 && (
                                <button
                                    className="action-btn-primary"
                                    style={{ backgroundColor: '#28a745' }}
                                    onClick={() => setShowPaymentModal(true)}
                                >
                                    <IoCashOutline size={18} />
                                    Registrar Pago (${new Intl.NumberFormat('es-MX').format(deudaSeleccionada)})
                                </button>
                            )}
                        </div>

                        {/* Desktop Table View */}
                        <div className="table-responsive hide-on-mobile">
                            <table className="modern-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}></th>
                                        <th className="hide-on-mobile">Estatus</th>
                                        <th className="col-concepto">Concepto</th>
                                        <th className="hide-on-mobile">Vencimiento</th>
                                        <th className="col-monto">Monto</th>
                                        <th className="hide-on-mobile">Pagado El</th>
                                        <th className="hide-on-mobile">Método</th>
                                        <th className="col-acciones"></th>
                                    </tr>
                                    <tr className="filter-row hide-on-mobile" style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                        <th></th>
                                        <th>
                                            <select className="filter-input-small" value={cargosFilters.estatus} onChange={e => setCargosFilters({ ...cargosFilters, estatus: e.target.value })}>
                                                <option value="">Todos</option>
                                                <option value="vencida">Vencida ⚠️</option>
                                                <option value="pendiente">Pendiente</option>
                                                <option value="parcial">Parcial</option>
                                                <option value="pagado">Pagado ✅</option>
                                                <option value="cancelado">Cancelado</option>
                                            </select>
                                        </th>
                                        <th>
                                            <input type="text" className="filter-input-small" placeholder="Buscar concepto..." value={cargosFilters.concepto} onChange={e => setCargosFilters({ ...cargosFilters, concepto: e.target.value })} />
                                        </th>
                                        <th>
                                            <input type="text" className="filter-input-small" placeholder="Ej: <10/12/26, 15/05/26" value={cargosFilters.vencimiento} onChange={e => setCargosFilters({ ...cargosFilters, vencimiento: e.target.value })} title="Filtro inteligente: Escribe una fecha (ej. 15/08) o usa operadores como < o > seguido de DD/MM/YYYY" />
                                        </th>
                                        <th>
                                            <input type="text" className="filter-input-small" placeholder="Ej: >500, 1000" value={cargosFilters.monto} onChange={e => setCargosFilters({ ...cargosFilters, monto: e.target.value })} />
                                        </th>
                                        <th></th>
                                        <th>
                                            <select className="filter-input-small" value={cargosFilters.metodo} onChange={e => setCargosFilters({ ...cargosFilters, metodo: e.target.value })}>
                                                <option value="">Todos</option>
                                                <option value="stripe">Stripe</option>
                                                <option value="efectivo">Efectivo</option>
                                                <option value="transferencia">Transferencia</option>
                                                <option value="cheque">Cheque</option>
                                            </select>
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCargos.map(cargo => {
                                        const isExpired = cargo.estatus === 'pendiente' && cargo.fechaVencimiento && moment().isAfter(moment(cargo.fechaVencimiento.toDate()));
                                        const displayStatus = isExpired ? 'vencida' : cargo.estatus;

                                        return (
                                            <tr key={cargo.id} style={{ backgroundColor: selectedCharges.includes(cargo.id) ? '#f0f9ff' : 'transparent' }}>
                                                <td>
                                                    {(cargo.estatus === 'pendiente' || cargo.estatus === 'parcial') && (
                                                        <input type="checkbox" checked={selectedCharges.includes(cargo.id)} onChange={() => toggleChargeSelection(cargo.id)} style={{ width: 16, height: 16 }} />
                                                    )}
                                                </td>
                                                <td className="hide-on-mobile"><StatusBadge status={displayStatus} /></td>
                                                <td className="col-concepto">
                                                    <div style={{ fontWeight: 600 }}>{cargo.tipo}</div>
                                                    <div style={{ fontSize: '0.85em', color: '#666' }}>{cargo.descripcion}</div>
                                                    <div className="detalle-mobile-estatus">
                                                        <StatusBadge status={displayStatus} />
                                                        <span style={{ fontSize: '0.8em', color: '#888', marginLeft: 6 }}>{moment(cargo.fechaVencimiento.toDate()).format('DD/MM/YY')}</span>
                                                    </div>
                                                </td>
                                                <td className="hide-on-mobile">{moment(cargo.fechaVencimiento.toDate()).format('DD MMM YYYY')}</td>
                                                <td className="col-monto">
                                                    <span style={{ fontWeight: 'bold', color: isExpired ? '#dc2626' : (cargo.estatus === 'pagado' ? '#16a34a' : '#1f2937') }}>
                                                        ${new Intl.NumberFormat('es-MX').format(cargo.monto)}
                                                    </span>
                                                    {cargo.estatus === 'parcial' && (
                                                        <div style={{ fontSize: '0.8em', color: '#d9534f' }}>
                                                            Saldo: ${new Intl.NumberFormat('es-MX').format(cargo.monto - (cargo.montoPagado || 0))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="hide-on-mobile">{cargo.fechaPago ? moment(cargo.fechaPago.toDate()).format('DD/MM/YY HH:mm') : '-'}</td>
                                                <td className="hide-on-mobile">{cargo.metodoPago || '-'}</td>
                                                <td className="col-acciones">
                                                    <button onClick={() => setEditingRef({ id: cargo.id, type: 'CARGO' })} className="glass-icon-btn" title="Editar">
                                                        <IoPencil />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Charges Cards View */}
                        <div className="cargos-cards-mobile show-on-mobile-flex">
                            {filteredCargos.map(cargo => {
                                const isExpired = cargo.estatus === 'pendiente' && cargo.fechaVencimiento && moment().isAfter(moment(cargo.fechaVencimiento.toDate()));
                                const displayStatus = isExpired ? 'vencida' : cargo.estatus;

                                // Dynamically map concept types to icons
                                let ConceptIcon = IoWalletOutline;
                                const conceptoLower = `${cargo.tipo || ''} ${cargo.descripcion || ''}`.toLowerCase();
                                if (conceptoLower.includes('multa')) {
                                    ConceptIcon = IoWarning;
                                } else if (conceptoLower.includes('extraordinaria') || conceptoLower.includes('extraordinario')) {
                                    ConceptIcon = IoBriefcaseOutline;
                                } else if (conceptoLower.includes('cuota') || conceptoLower.includes('mantenimiento')) {
                                    ConceptIcon = IoHomeOutline;
                                }

                                return (
                                    <div 
                                        key={cargo.id} 
                                        className={`cargo-card-premium cargo-status-${displayStatus}`}
                                        style={{ backgroundColor: selectedCharges.includes(cargo.id) ? '#f0f9ff' : '#ffffff' }}
                                    >
                                        <div className="cargo-card-selection-row">
                                            {(cargo.estatus === 'pendiente' || cargo.estatus === 'parcial') && (
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedCharges.includes(cargo.id)} 
                                                    onChange={() => toggleChargeSelection(cargo.id)} 
                                                    style={{ width: 18, height: 18, cursor: 'pointer' }} 
                                                />
                                            )}
                                            <div className="cargo-card-concept-badge">
                                                <ConceptIcon size={16} />
                                                <span>{cargo.tipo}</span>
                                            </div>
                                            <div className="cargo-card-actions-col">
                                                <button onClick={() => setEditingRef({ id: cargo.id, type: 'CARGO' })} className="glass-icon-btn" title="Editar">
                                                    <IoPencil />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="cargo-card-body">
                                            <p className="cargo-card-desc">{cargo.descripcion}</p>
                                        </div>

                                        <div className="cargo-card-footer">
                                            <div className="cargo-card-footer-info">
                                                <StatusBadge status={displayStatus} />
                                                <span className="cargo-card-date-label">
                                                    {cargo.fechaPago 
                                                        ? `Pagado: ${moment(cargo.fechaPago.toDate()).format('DD/MM/YY')}` 
                                                        : `Vence: ${moment(cargo.fechaVencimiento.toDate()).format('DD/MM/YY')}`}
                                                </span>
                                            </div>
                                            <div className="cargo-card-price-col">
                                                <span className={`cargo-card-price ${isExpired ? 'vencido' : (cargo.estatus === 'pagado' ? 'pagado' : '')}`}>
                                                    ${new Intl.NumberFormat('es-MX').format(cargo.monto)}
                                                </span>
                                                {cargo.estatus === 'parcial' && (
                                                    <div className="cargo-card-balance">
                                                        Saldo: ${new Intl.NumberFormat('es-MX').format(cargo.monto - (cargo.montoPagado || 0))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ marginTop: '40px', marginBottom: '10px', padding: '0 20px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937' }}>Historial de Pagos y Abonos</h3>
                        </div>
                        {pagos.length === 0 ? <div className="empty-state" style={{ padding: 20 }}>No hay pagos registrados.</div> : (
                            <>
                                {/* Desktop Table View */}
                                <div className="table-responsive hide-on-mobile">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Concepto</th>
                                                <th>Referencia</th>
                                                <th>Monto</th>
                                                <th>Detalle Aplicación</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagos.map(pago => (
                                                <tr key={pago.id}>
                                                    <td>{moment(pago.fecha.toDate()).format('DD/MM/YYYY')}</td>
                                                    <td>{pago.descripcion}</td>
                                                    <td className="hide-on-mobile">{pago.referencia || '-'}</td>
                                                    <td className="col-monto" style={{ fontWeight: 'bold', color: '#16a34a' }}>${new Intl.NumberFormat('es-MX').format(pago.monto)} </td>
                                                    <td className="col-detalle">
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            {pago.saldoFavorGenerado > 0 && (
                                                                <span className="badge badge-success" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>
                                                                    Saldo a Favor: ${pago.saldoFavorGenerado}
                                                                </span>
                                                            )}
                                                            {pago.distribucionPago && pago.distribucionPago.map((dist, idx) => (
                                                                dist.tipo === 'cargo' && (
                                                                    <span key={idx} style={{ fontSize: '0.85em', color: '#666' }}>
                                                                        • Pagó {dist.descripcion} (${dist.montoAplicado})
                                                                    </span>
                                                                )
                                                            ))}
                                                            {!pago.distribucionPago && !pago.saldoFavorGenerado && (
                                                                <span style={{ color: '#999', fontStyle: 'italic' }}>Sin aplicar</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="col-acciones">
                                                        <button onClick={() => setEditingRef({ id: pago.id, type: 'PAGO' })} className="glass-icon-btn" title="Editar Pago">
                                                            <IoPencil />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards View for Payments */}
                                <div className="abonos-cards-mobile show-on-mobile-flex">
                                    {pagos.map(pago => (
                                        <div key={pago.id} className="abono-card-premium">
                                            <div className="abono-card-header">
                                                <span className="abono-card-fecha">{moment(pago.fecha.toDate()).format('DD/MM/YYYY')}</span>
                                                <div className="abono-card-actions">
                                                    <button onClick={() => setEditingRef({ id: pago.id, type: 'PAGO' })} className="glass-icon-btn" title="Editar Pago">
                                                        <IoPencil />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="abono-card-body">
                                                <div className="abono-card-info-group">
                                                    <span className="abono-card-desc">{pago.descripcion}</span>
                                                    {pago.referencia && <span className="abono-card-ref">Ref: {pago.referencia}</span>}
                                                </div>
                                                <div className="abono-card-monto-col">
                                                    <span className="abono-card-monto">${new Intl.NumberFormat('es-MX').format(pago.monto)}</span>
                                                </div>
                                            </div>
                                            {(pago.saldoFavorGenerado > 0 || (pago.distribucionPago && pago.distribucionPago.length > 0)) && (
                                                <div className="abono-card-application">
                                                    <span className="application-title">Detalle de Aplicación</span>
                                                    <div className="application-items">
                                                        {pago.saldoFavorGenerado > 0 && (
                                                            <span className="badge badge-success" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', display: 'inline-flex', padding: '3px 8px', borderRadius: '6px', fontSize: '0.8em', fontWeight: 'bold' }}>
                                                                Saldo a Favor: ${pago.saldoFavorGenerado}
                                                            </span>
                                                        )}
                                                        {pago.distribucionPago && pago.distribucionPago.map((dist, idx) => (
                                                            dist.tipo === 'cargo' && (
                                                                <span key={idx} className="application-item-text">
                                                                    • Aplicado a {dist.descripcion} (${dist.montoAplicado})
                                                                </span>
                                                            )
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}

                {activeTab === 'perfil' && (
                    <>
                        <div className="datos-grid-premium">
                            <div className="dato-card-premium">
                                <div className="dato-card-icon" style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}>
                                    <IoMailOutline size={20} />
                                </div>
                                <div className="dato-card-info">
                                    <span className="dato-card-label">Email</span>
                                    <span className="dato-card-value">{usuario.email}</span>
                                </div>
                            </div>

                            <div className="dato-card-premium">
                                <div className="dato-card-icon" style={{ backgroundColor: '#f0fdf4', color: '#16a34a' }}>
                                    <IoCallOutline size={20} />
                                </div>
                                <div className="dato-card-info">
                                    <span className="dato-card-label">Teléfono</span>
                                    <span className="dato-card-value">{usuario.numeroCelular || usuario.telefono || 'No registrado'}</span>
                                </div>
                            </div>

                            <div className="dato-card-premium">
                                <div className="dato-card-icon" style={{ backgroundColor: '#faf5ff', color: '#7c3aed' }}>
                                    <IoPersonCircleOutline size={20} />
                                </div>
                                <div className="dato-card-info">
                                    <span className="dato-card-label">Rol del Sistema</span>
                                    <span className="dato-card-value" style={{ textTransform: 'capitalize' }}>{usuario.rol}</span>
                                </div>
                            </div>

                            <div className="dato-card-premium">
                                <div className="dato-card-icon" style={{ backgroundColor: '#fff7ed', color: '#ea580c' }}>
                                    <IoBriefcaseOutline size={20} />
                                </div>
                                <div className="dato-card-info">
                                    <span className="dato-card-label">Proveedor del Market</span>
                                    {proveedorInfo ? (
                                        <span className={`status-pill ${proveedorInfo.estatus === 'aprobado' || proveedorInfo.estatus === 'aprobado_admin' ? 'status-aldia' : 'inquilino'}`} style={{ display: 'inline-flex', marginTop: '4px' }}>
                                            {proveedorInfo.estatus.toUpperCase().replace('_', ' ')}
                                        </span>
                                    ) : (
                                        <span className="dato-card-value" style={{ color: '#94a3b8' }}>No registrado</span>
                                    )}
                                </div>
                            </div>

                            <div className="dato-card-premium">
                                <div className="dato-card-icon" style={{ backgroundColor: '#fdf2f8', color: '#db2777' }}>
                                    <IoKeyOutline size={20} />
                                </div>
                                <div className="dato-card-info">
                                    <span className="dato-card-label">Tipo de Residente</span>
                                    <span className="dato-card-value">{usuario.tipoResidente || 'Inquilino'}</span>
                                </div>
                            </div>

                            <div className="dato-card-premium">
                                <div className="dato-card-icon" style={{ backgroundColor: '#f0fdfa', color: '#0d9488' }}>
                                    <IoCalendarOutline size={20} />
                                </div>
                                <div className="dato-card-info">
                                    <span className="dato-card-label">Fecha de Registro</span>
                                    <span className="dato-card-value">{usuario.fechaRegistro ? moment(usuario.fechaRegistro.toDate()).format('LL') : '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* --- SUSPENSION DE REPORTES SECTION --- */}
                        {usuario.rol === 'residente' && (
                            <div className="dato-card-premium" style={{ width: '100%', display: 'block', padding: '20px', marginTop: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ 
                                            width: '44px', 
                                            height: '44px', 
                                            borderRadius: '12px', 
                                            backgroundColor: usuario.suspensionReportes?.activo && moment().isBefore(moment(usuario.suspensionReportes.hasta.toDate())) ? '#fee2e2' : '#dcfce7', 
                                            color: usuario.suspensionReportes?.activo && moment().isBefore(moment(usuario.suspensionReportes.hasta.toDate())) ? '#dc2626' : '#16a34a', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' 
                                        }}>
                                            <IoWarning size={22} />
                                        </div>
                                        <div>
                                            <h4 style={{ margin: 0, fontSize: '1rem', color: '#1f2937', fontWeight: 'bold' }}>Acceso al Módulo de Reportes</h4>
                                            <p style={{ margin: '3px 0 0 0', fontSize: '0.85rem' }}>
                                                {usuario.suspensionReportes?.activo && moment().isBefore(moment(usuario.suspensionReportes.hasta.toDate())) ? (
                                                    <span style={{ color: '#dc2626', fontWeight: '600' }}>⚠️ Suspendido</span>
                                                ) : (
                                                    <span style={{ color: '#16a34a', fontWeight: '600' }}>✅ Activo</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        {usuario.suspensionReportes?.activo && moment().isBefore(moment(usuario.suspensionReportes.hasta.toDate())) ? (
                                            <button 
                                                className="action-btn-primary" 
                                                style={{ backgroundColor: '#10b981', display: 'inline-flex', alignItems: 'center', gap: '5px' }} 
                                                onClick={handleLevantarSuspension}
                                            >
                                                Habilitar Módulo
                                            </button>
                                        ) : (
                                            <button 
                                                className="action-btn-primary" 
                                                style={{ backgroundColor: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: '5px' }} 
                                                onClick={() => setShowSuspensionModal(true)}
                                            >
                                                Suspender Acceso
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {usuario.suspensionReportes?.activo && moment().isBefore(moment(usuario.suspensionReportes.hasta.toDate())) && (
                                    <div style={{ marginTop: '15px', padding: '15px', background: '#ffffff', borderRadius: '12px', borderLeft: '4px solid #ef4444', border: '1px solid #e2e8f0', borderLeftWidth: '5px', fontSize: '0.9rem' }}>
                                        <div style={{ color: '#4b5563', marginBottom: '6px', fontWeight: 'bold' }}>
                                            Detalles de la suspensión:
                                        </div>
                                        <div style={{ color: '#1f2937', marginBottom: '4px' }}>
                                            <strong>Motivo:</strong> {usuario.suspensionReportes.motivo}
                                        </div>
                                        <div style={{ color: '#1f2937' }}>
                                            <strong>Expira el:</strong> {moment(usuario.suspensionReportes.hasta.toDate()).format('DD/MM/YYYY HH:mm')} ({moment(usuario.suspensionReportes.hasta.toDate()).fromNow()})
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'reportes' && (
                    <div className="reportes-section">
                        {reportes.length === 0 ? <p className="empty-state">No hay reportes.</p> : (
                            <>
                                {/* Desktop Table view */}
                                <div className="table-responsive hide-on-mobile">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '15%' }}>Fecha</th>
                                                <th style={{ width: '15%' }}>Tipo</th>
                                                <th style={{ width: '40%' }}>Descripción</th>
                                                <th style={{ width: '15%' }}>Estatus</th>
                                                <th style={{ width: '15%' }}>Respuesta Admin</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportes.map(rep => (
                                                <tr key={rep.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{moment(rep.fechaCreacion.toDate()).format('DD/MM/YYYY HH:mm')}</td>
                                                    <td style={{ fontWeight: 600, color: '#6366f1' }}>{rep.tipo}</td>
                                                    <td style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.4' }}>
                                                        <div>{rep.descripcion}</div>
                                                        {rep.comentariosAdmin && (
                                                            <div style={{ marginTop: '6px', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #6366f1', fontSize: '0.85em', color: '#475569' }}>
                                                                <strong>Admin:</strong> {rep.comentariosAdmin}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <StatusBadge status={rep.estatus === 'Solucionado' ? 'pagado' : 'pendiente'} />
                                                    </td>
                                                    <td>
                                                        {rep.comentariosAdmin ? (
                                                            <span className="respuesta-admin-badge si">
                                                                <IoCheckmarkCircleOutline /> Respondido
                                                            </span>
                                                        ) : (
                                                            <span className="respuesta-admin-badge no">
                                                                <IoTimeOutline /> Sin responder
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile cards view */}
                                <div className="reportes-cards-mobile show-on-mobile-flex">
                                    {reportes.map(rep => (
                                        <div key={rep.id} className="reporte-card-premium">
                                            <div className="reporte-card-header">
                                                <span className="reporte-card-tipo">{rep.tipo}</span>
                                                <span className="reporte-card-fecha">{moment(rep.fechaCreacion.toDate()).format('DD/MM/YY HH:mm')}</span>
                                            </div>
                                            <div className="reporte-card-body">
                                                <p className="reporte-card-desc">{rep.descripcion}</p>
                                            </div>
                                            <div className="reporte-card-footer">
                                                <StatusBadge status={rep.estatus === 'Solucionado' ? 'pagado' : 'pendiente'} />
                                                {rep.comentariosAdmin ? (
                                                    <span className="respuesta-admin-badge si">
                                                        <IoCheckmarkCircleOutline /> Respondido
                                                    </span>
                                                ) : (
                                                    <span className="respuesta-admin-badge no">
                                                        Sin respuesta
                                                    </span>
                                                )}
                                            </div>
                                            {rep.comentariosAdmin && (
                                                <div className="reporte-card-reply">
                                                    <strong>Respuesta de Administración:</strong>
                                                    <p>{rep.comentariosAdmin}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'vehiculos' && (
                    <div className="vehiculos-section">
                        {vehiculos.length === 0 ? <p className="empty-state">No hay vehículos.</p> : (
                            <>
                                {/* Desktop Table view */}
                                <div className="table-responsive hide-on-mobile">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Marca/Modelo</th>
                                                <th>Placas</th>
                                                <th>Color</th>
                                                <th>Estatus</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {vehiculos.map(v => (
                                                <tr key={v.id}>
                                                    <td>{v.marca} {v.modelo} {v.anio}</td>
                                                    <td>{v.placas}</td>
                                                    <td>{v.color}</td>
                                                    <td>
                                                        <span className={`vehiculo-status-badge ${v.estatus === 'activo' ? 'activo' : 'inactivo'}`}>
                                                            {v.estatus === 'activo' ? 'Activo' : 'Inactivo'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile cards view */}
                                <div className="vehiculos-cards-mobile show-on-mobile-flex">
                                    {vehiculos.map(v => (
                                        <div key={v.id} className="vehiculo-card-premium">
                                            <div className="vehiculo-card-header">
                                                <div className="vehiculo-card-avatar">
                                                    <IoCarSportOutline size={22} />
                                                </div>
                                                <div className="vehiculo-card-title-group">
                                                    <span className="vehiculo-card-model">{v.marca} {v.modelo}</span>
                                                    <span className="vehiculo-card-year">{v.anio || 'Año N/A'}</span>
                                                </div>
                                                <span className={`vehiculo-status-badge ${v.estatus === 'activo' ? 'activo' : 'inactivo'}`}>
                                                    {v.estatus === 'activo' ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>
                                            <div className="vehiculo-card-body">
                                                <div className="vehiculo-card-info-item">
                                                    <span className="info-label">Color</span>
                                                    <span className="info-value">{v.color || 'No especificado'}</span>
                                                </div>
                                                <div className="vehiculo-card-info-item plate-item">
                                                    <span className="info-label">Placas</span>
                                                    <div className="vehiculo-placa-badge">
                                                        <span className="placa-text">{v.placas}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'reservas' && (
                    <div className="reservas-section">
                        {reservas.length === 0 ? <p className="empty-state">No hay reservaciones.</p> : (
                            <>
                                {/* Desktop Table view */}
                                <div className="table-responsive hide-on-mobile">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Amenidad</th>
                                                <th>Inicio</th>
                                                <th>Fin</th>
                                                <th>Personas</th>
                                                <th>Estatus</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reservas.map(res => (
                                                <tr key={res.id}>
                                                    <td style={{ fontWeight: 600, color: '#4f46e5' }}>
                                                        {amenidadesMap[res.amenidadId] || 'Cargando Amenidad...'}
                                                    </td>
                                                    <td>{res.fechaInicio ? moment(res.fechaInicio.toDate()).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                                    <td>{res.fechaFin ? moment(res.fechaFin.toDate()).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: '600' }}>
                                                        <IoPeopleOutline style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                                        {res.numeroUsuarios}
                                                    </td>
                                                    <td><StatusBadge status={res.estatus || 'Confirmada'} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                             {/* Mobile cards view */}
                                <div className="reservas-cards-mobile show-on-mobile-flex">
                                    {reservas.map(res => (
                                        <div key={res.id} className="reserva-card-premium">
                                            <div className="reserva-card-header">
                                                <span className="reserva-card-amenidad">
                                                    {amenidadesMap[res.amenidadId] || 'Cargando Amenidad...'}
                                                </span>
                                                <StatusBadge status={res.estatus || 'Confirmada'} />
                                            </div>
                                            <div className="reserva-card-body">
                                                <div className="reserva-card-time-group">
                                                    <div className="time-item">
                                                        <span className="time-label">Inicio</span>
                                                        <span className="time-value">
                                                            {res.fechaInicio ? moment(res.fechaInicio.toDate()).format('D MMM, HH:mm [hrs]') : '-'}
                                                        </span>
                                                    </div>
                                                    <div className="time-item">
                                                        <span className="time-label">Fin</span>
                                                        <span className="time-value">
                                                            {res.fechaFin ? moment(res.fechaFin.toDate()).format('D MMM, HH:mm [hrs]') : '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="reserva-card-footer">
                                                <span className="reserva-card-asistentes">
                                                    <IoPeopleOutline size={18} style={{ marginRight: '6px', verticalAlign: 'middle', color: '#6366f1' }} />
                                                    <strong>{res.numeroUsuarios}</strong> {res.numeroUsuarios === 1 ? 'Persona' : 'Personas'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'apercibimientos' && (
                    <div style={{ padding: '20px' }}>
                        <h3 style={{ marginBottom: '20px', color: '#1f2937' }}>Historial de Apercibimientos y Multas</h3>
                        {apercibimientos.length === 0 ? (
                            <div className="empty-state" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                                No se registran apercibimientos ni multas para este residente.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {apercibimientos.map(item => (
                                    <div 
                                        key={item.id} 
                                        style={{ 
                                            background: '#f8fafc', 
                                            borderRadius: '12px', 
                                            borderLeft: `5px solid ${item.tipo === 'multa' ? '#ef4444' : '#f59e0b'}`,
                                            padding: '16px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem' }}>
                                            <span style={{ 
                                                textTransform: 'uppercase', 
                                                fontWeight: 'bold', 
                                                color: item.tipo === 'multa' ? '#ef4444' : '#d97706' 
                                            }}>
                                                {item.tipo}
                                            </span>
                                            <span style={{ color: '#64748b' }}>
                                                {item.fechaCreacion?.toDate ? moment(item.fechaCreacion.toDate()).format('D MMM YYYY, h:mm a') : ''}
                                            </span>
                                        </div>
                                        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#1e293b' }}>
                                            Categoría: {item.categoria}
                                        </p>
                                        <p style={{ margin: '0 0 10px 0', fontSize: '0.92rem', color: '#475569', lineHeight: '1.4' }}>
                                            {item.descripcion}
                                        </p>
                                        {item.evidenciaUrl && (
                                            <a href={item.evidenciaUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.88rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
                                                Ver evidencia adjunta
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'convenios' && (
                    <div style={{ padding: '20px' }}>
                        <h3 style={{ marginBottom: '20px', color: '#1f2937' }}>Convenios de Pago del Residente</h3>
                        {convenios.length === 0 ? (
                            <div className="empty-state" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                                No se registran convenios de pago para este residente.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {convenios.map(item => (
                                    <div 
                                        key={item.id} 
                                        style={{ 
                                            background: '#f8fafc', 
                                            borderRadius: '12px', 
                                            borderLeft: `5px solid ${item.estatus === 'activo' ? '#10b981' : (item.estatus === 'completado' ? '#3b82f6' : '#ef4444')}`,
                                            padding: '16px',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85rem' }}>
                                            <span style={{ 
                                                textTransform: 'uppercase', 
                                                fontWeight: 'bold', 
                                                color: item.estatus === 'activo' ? '#10b981' : (item.estatus === 'completado' ? '#3b82f6' : '#ef4444')
                                            }}>
                                                {item.estatus}
                                            </span>
                                            <span style={{ color: '#64748b' }}>
                                                {item.fechaCreacion?.toDate ? moment(item.fechaCreacion.toDate()).format('D MMM YYYY, h:mm a') : ''}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#334155', fontSize: '0.95rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <IoCashOutline style={{ color: '#64748b' }} />
                                                <span>Refinanciado: <strong>${new Intl.NumberFormat('es-MX').format(item.montoTotalRefinanciado)} MXN</strong> a <strong>{item.numeroMensualidades} meses</strong></span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <IoCashOutline style={{ color: '#64748b' }} />
                                                <span>Cuota Mensual: <strong>${new Intl.NumberFormat('es-MX').format(item.montoMensual)} MXN</strong></span>
                                            </div>
                                        </div>
                                        {item.evidenciaFirmaUrl && (
                                            <div style={{ marginTop: '12px' }}>
                                                <a href={item.evidenciaFirmaUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.88rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <IoDocumentTextOutline size={16} /> Ver Convenio Firmado
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* --- PAYMENT MODAL --- */}
            {showPaymentModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '600px', padding: 30, borderRadius: 20 }}>
                        <button className="modal-top-close-button" onClick={() => setShowPaymentModal(false)}>
                            <IoClose />
                        </button>
                        <h2>Registrar Pago</h2>
                        <p style={{ color: '#666', marginBottom: '10px' }}>
                            Total a Pagar: <strong style={{ color: '#28a745', fontSize: '1.2em' }}>${new Intl.NumberFormat('es-MX').format(deudaSeleccionada)}</strong>
                        </p>

                        {/* Modal Tabs */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #eee' }}>
                            <button
                                style={{ padding: '10px', borderBottom: paymentMode === 'manual' ? '2px solid #007bff' : 'none', fontWeight: paymentMode === 'manual' ? 'bold' : 'normal', background: 'none', border: 'none', cursor: 'pointer', color: paymentMode === 'manual' ? '#007bff' : '#555' }}
                                onClick={() => setPaymentMode('manual')}
                            >
                                Pago Manual
                            </button>
                            <button
                                style={{ padding: '10px', borderBottom: paymentMode === 'bank' ? '2px solid #007bff' : 'none', fontWeight: paymentMode === 'bank' ? 'bold' : 'normal', background: 'none', border: 'none', cursor: 'pointer', color: paymentMode === 'bank' ? '#007bff' : '#555' }}
                                onClick={() => setPaymentMode('bank')}
                            >
                                Vincular Depósito
                            </button>
                        </div>

                        {paymentMode === 'manual' ? (
                            <>
                                <div className="form-group">
                                    <label>Fecha de Pago:</label>
                                    <input
                                        type="datetime-local"
                                        className="form-input"
                                        value={paymentDetails.fechaPago}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, fechaPago: e.target.value })}
                                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', marginTop: 5 }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Método de Pago:</label>
                                    <select
                                        className="form-input"
                                        value={paymentDetails.metodoPago}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, metodoPago: e.target.value })}
                                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', marginTop: 5 }}
                                    >
                                        <option value="Transferencia">Transferencia Bancaria</option>
                                        <option value="Efectivo">Efectivo</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="Depósito">Depósito en Ventanilla</option>
                                        <option value="Otro">Otro</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Motivo / Descripción (Obligatorio):</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Ej: Pago en efectivo entregado en oficina..."
                                        value={paymentDetails.referencia}
                                        onChange={(e) => setPaymentDetails({ ...paymentDetails, referencia: e.target.value })}
                                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', marginTop: 5 }}
                                    />
                                    {!paymentDetails.referencia.trim() && (
                                        <small style={{ color: 'red' }}>* Requerido para auditoría</small>
                                    )}
                                </div>
                            </>
                        ) : (
                            // BANK MODE
                            <div className="bank-list-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                                    Selecciona un depósito "Huérfano" para asignar a este pago.
                                </p>
                                {unappliedDeposits.length === 0 ? (
                                    <p>No se encontraron depósitos sin aplicar recientes.</p>
                                ) : (
                                    <table className="modern-table" style={{ fontSize: '0.85em' }}>
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Ref</th>
                                                <th>Monto</th>
                                                <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {unappliedDeposits.map(dep => (
                                                <tr key={dep.id} style={{ backgroundColor: selectedDepositId === dep.id ? '#e8f0fe' : 'white' }}>
                                                    <td>{moment(dep.fecha.toDate()).format('DD/MM/YYYY')}</td>
                                                    <td>{dep.referencia}</td>
                                                    <td style={{ fontWeight: 'bold' }}>${dep.monto}</td>
                                                    <td>
                                                        <button
                                                            onClick={() => setSelectedDepositId(dep.id)}
                                                            style={{
                                                                padding: '4px 8px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #007bff',
                                                                background: selectedDepositId === dep.id ? '#007bff' : 'white',
                                                                color: selectedDepositId === dep.id ? 'white' : '#007bff',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {selectedDepositId === dep.id ? 'Seleccionado' : 'Elegir'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                            <button
                                className="action-btn-primary"
                                style={{ backgroundColor: '#28a745', width: '100%', justifyContent: 'center' }}
                                onClick={handleRegisterPayment}
                                disabled={
                                    (paymentMode === 'manual' && !paymentDetails.referencia.trim()) ||
                                    (paymentMode === 'bank' && !selectedDepositId)
                                }
                            >
                                {paymentMode === 'manual' ? 'Registrar Manualmente' : 'Vincular y Conciliar'}
                            </button>
                            <button
                                className="action-btn-secondary"
                                style={{ backgroundColor: '#6c757d', width: '100%', justifyContent: 'center' }}
                                onClick={() => setShowPaymentModal(false)}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- SUSPENSION MODAL --- */}
            {showSuspensionModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px', padding: 30, borderRadius: 20 }}>
                        <button className="modal-top-close-button" onClick={() => {
                            setShowSuspensionModal(false);
                            setSuspensionMotivo('');
                        }}>
                            <IoClose />
                        </button>
                        <h2>Suspender Acceso a Reportes</h2>
                        <p style={{ color: '#666', marginBottom: '20px', fontSize: '0.9rem' }}>
                            El residente no podrá levantar reportes de mantenimiento, quejas o sugerencias durante el tiempo seleccionado.
                        </p>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ fontWeight: '500', display: 'block', marginBottom: '5px' }}>Duración de la Suspensión:</label>
                            <select
                                className="form-input"
                                value={suspensionDuracion}
                                onChange={(e) => setSuspensionDuracion(e.target.value)}
                                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', background: 'white' }}
                            >
                                <option value="1_dia">1 Día</option>
                                <option value="1_semana">1 Semana</option>
                                <option value="1_mes">1 Mes</option>
                                <option value="1_ano">1 Año</option>
                            </select>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: '500', display: 'block', marginBottom: '5px' }}>Motivo (Obligatorio):</label>
                            <textarea
                                className="form-input"
                                rows="3"
                                placeholder="Describa el motivo del bloqueo..."
                                value={suspensionMotivo}
                                onChange={(e) => setSuspensionMotivo(e.target.value)}
                                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', resize: 'vertical', fontFamily: 'inherit' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                            <button
                                className="action-btn-primary"
                                style={{ backgroundColor: '#ef4444', width: '100%', justifyContent: 'center' }}
                                onClick={handleSuspenderReportes}
                                disabled={isSavingSuspension || !suspensionMotivo.trim()}
                            >
                                {isSavingSuspension ? 'Aplicando...' : 'Aplicar Suspensión'}
                            </button>
                            <button
                                className="action-btn-secondary"
                                style={{ backgroundColor: '#6c757d', width: '100%', justifyContent: 'center' }}
                                onClick={() => {
                                    setShowSuspensionModal(false);
                                    setSuspensionMotivo('');
                                }}
                                disabled={isSavingSuspension}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- EDIT MODAL --- */}
            {editingRef && (
                <EditarTransaccionModal
                    id={editingRef.id}
                    type={editingRef.type}
                    onClose={() => setEditingRef(null)}
                    onSuccess={handleEditSuccess}
                />
            )}
                    </div>
                </div>
            </main>
            <KondoBottomNav activeRoute="/gestion-usuarios" />
        </div>
    );
}

export default DetalleUsuarioScreen;
